/**
 * Stylised SVG map of Great Britain with clickable farm markers.
 * Lightweight — no map SDK. Markers positioned from farm.map_x / map_y (percent).
 * Every marker has: a large invisible hit area, a pulsing ring, and a clickable
 * farm-name label so it is obvious each farm can be selected.
 */
export const UKMap = ({ farms, selectedId, onSelect, dark = true }) => {
    const land = dark ? "#1C2B23" : "#DCD3C2";
    const stroke = dark ? "rgba(201,179,122,0.35)" : "rgba(11,11,12,0.25)";

    // Place labels so they don't collide or fall off the map
    const labelSide = (farm) => (farm.map_x > 48 ? "right" : "left");

    return (
        <div className="relative w-full max-w-[420px] mx-auto" data-testid="provenance-map">
            <svg viewBox="0 0 100 124" className="w-full h-auto" role="img" aria-label="Map of Britain showing our partner farms — select a farm to explore it">
                <path
                    d="M52,4 L58,9 L56,14 L61,20 L57,26 L63,32 L60,38 L55,42 L58,47 L63,52 L66,58 L69,64 L72,70 L75,76 L73,81 L79,86 L77,92 L74,97 L68,101 L58,103 L50,101 L44,104 L38,106 L30,112 L22,118 L20,115 L26,109 L33,103 L39,99 L37,95 L31,93 L25,88 L27,82 L33,78 L37,74 L40,69 L43,64 L41,58 L43,53 L39,49 L34,46 L38,41 L31,37 L36,31 L29,26 L34,20 L40,14 L44,9 Z"
                    fill={land}
                    stroke={stroke}
                    strokeWidth="0.6"
                    strokeLinejoin="round"
                />
                {farms.map((farm) => {
                    const cx = farm.map_x;
                    const cy = farm.map_y * 1.24;
                    const active = selectedId === farm.id;
                    const side = labelSide(farm);
                    const labelX = side === "right" ? cx + 4.5 : cx - 4.5;
                    return (
                        <g
                            key={farm.id}
                            className="map-marker-group"
                            role="button"
                            tabIndex="0"
                            aria-label={`Select ${farm.name} in ${farm.region}`}
                            data-testid={`provenance-map-marker-${farm.id}`}
                            style={{ cursor: "pointer" }}
                            onClick={() => onSelect(farm.id)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") onSelect(farm.id);
                            }}
                        >
                            {/* generous invisible hit area */}
                            <circle cx={cx} cy={cy} r="6.5" fill="transparent" />
                            {/* pulsing ring — on every marker so all look tappable */}
                            <circle
                                cx={cx}
                                cy={cy}
                                r="3.2"
                                fill="none"
                                stroke={active ? "#C9B37A" : dark ? "rgba(246,241,231,0.5)" : "rgba(15,42,31,0.4)"}
                                strokeWidth="0.5"
                                className="marker-pulse"
                            />
                            <circle
                                cx={cx}
                                cy={cy}
                                r={active ? 2.6 : 2}
                                fill={active ? "#C9B37A" : dark ? "#F6F1E7" : "#0F2A1F"}
                                stroke={active ? "#0B0B0C" : "transparent"}
                                strokeWidth="0.4"
                                style={{ transition: "r 200ms ease-out, fill 200ms ease-out" }}
                            />
                            {/* clickable farm label */}
                            <text
                                x={labelX}
                                y={cy + 1.1}
                                textAnchor={side === "right" ? "start" : "end"}
                                className="map-label"
                                style={{
                                    fontFamily: "Manrope, sans-serif",
                                    fontSize: "3.1px",
                                    fontWeight: 600,
                                    letterSpacing: "0.08em",
                                    fill: active ? "#C9B37A" : dark ? "rgba(246,241,231,0.75)" : "rgba(11,11,12,0.7)",
                                    textDecorationLine: active ? "none" : "underline",
                                    textDecorationColor: dark ? "rgba(201,179,122,0.5)" : "rgba(15,42,31,0.4)",
                                    textUnderlineOffset: "1px",
                                    transition: "fill 200ms ease-out",
                                }}
                            >
                                {farm.region.toUpperCase()}
                            </text>
                        </g>
                    );
                })}
            </svg>
            <p className={`font-mono-meta text-[9px] tracking-[0.25em] text-center mt-3 ${dark ? "text-white/40" : "text-black/40"}`}>
                SELECT A FARM TO EXPLORE
            </p>
        </div>
    );
};
